import React, { useState } from 'react';

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

export default function DateTimePicker({ value, onChange, onClose }) {
  const today = new Date();
  const initial = value ? new Date(value) : null;

  const [viewDate, setViewDate] = useState(() => {
    if (initial) return new Date(initial.getFullYear(), initial.getMonth(), 1);
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(initial ? initial.getDate() : null);
  const [hour, setHour] = useState(() => {
    if (initial) return initial.getHours();
    const now = new Date();
    return now.getMinutes() >= 55 ? (now.getHours() + 1) % 24 : now.getHours();
  });
  const [minute, setMinute] = useState(() => {
    if (initial) return initial.getMinutes();
    const now = new Date();
    return (Math.ceil(now.getMinutes() / 5) * 5) % 60;
  });

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  // Проверка: дата в прошлом?
  const isPastDay = (day) => {
    const d = new Date(year, month, day);
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return d < todayStart;
  };

  // Проверка: момент в прошлом?
  const isPastTime = () => {
    if (!selectedDay) return true;
    const dt = new Date(year, month, selectedDay, hour, minute);
    return dt <= new Date();
  };

  // Проверка: сегодняшний день?
  const isToday = selectedDay &&
    year === today.getFullYear() &&
    month === today.getMonth() &&
    selectedDay === today.getDate();

  // Проверка: указанное время для сегодня уже прошло?
  const isTimePastToday = (h, m) => {
    if (!isToday) return false;
    const now = new Date();
    return h < now.getHours() || (h === now.getHours() && m <= now.getMinutes());
  };

  const handleConfirm = () => {
    if (!selectedDay || isPastTime()) return;
    const dt = new Date(year, month, selectedDay, hour, minute);
    onChange(dt.toISOString());
    onClose();
  };

  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  const formatDisplay = () => {
    if (!selectedDay) return 'Выберите дату и время';
    const d = String(selectedDay).padStart(2, '0');
    const m = String(month + 1).padStart(2, '0');
    const h = String(hour).padStart(2, '0');
    const min = String(minute).padStart(2, '0');
    return `${d}.${m}.${year} ${h}:${min}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-t-2xl bg-white p-4 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Заголовок */}
        <div className="mb-3 flex items-center justify-between">
          <button onClick={prevMonth} className="p-2 text-[#64748b] hover:text-[#0f172a]">
            ←
          </button>
          <span className="text-sm font-semibold text-[#0f172a]">
            {monthNames[month]} {year}
          </span>
          <button onClick={nextMonth} className="p-2 text-[#64748b] hover:text-[#0f172a]">
            →
          </button>
        </div>

        {/* Дни недели */}
        <div className="mb-1 grid grid-cols-7 gap-1">
          {DAYS.map(d => (
            <div key={d} className="text-center text-[10px] font-medium text-[#64748b]">
              {d}
            </div>
          ))}
        </div>

        {/* Дни */}
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay }, (_, i) => (
            <div key={`empty-${i}`} />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isSelected = selectedDay === day;
            const isToday = dateStr === `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const isPast = isPastDay(day);

            return (
              <button
                key={day}
                onClick={() => !isPast && setSelectedDay(day)}
                disabled={isPast}
                className={`h-9 w-full rounded-lg text-sm transition ${
                  isSelected
                    ? 'bg-[#374151] text-white font-semibold'
                    : isPast
                    ? 'text-[#64748b]/40 cursor-not-allowed'
                    : isToday
                    ? 'bg-[#374151]/10 text-[#374151] font-medium'
                    : 'text-[#0f172a] hover:bg-[#f1f5f9]'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Время */}
        <div className="mt-3 flex items-center justify-center gap-3">
          <span className="text-xs text-[#64748b]">Время:</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setHour(h => (h + 23) % 24)}
              className="grid h-8 w-8 place-items-center rounded-lg bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]"
            >
              −
            </button>
            <span className={`w-10 text-center text-sm font-semibold ${isTimePastToday(hour, minute) ? 'text-[#D64545]' : 'text-[#0f172a]'}`}>
              {String(hour).padStart(2, '0')}
            </span>
            <button
              onClick={() => setHour(h => (h + 1) % 24)}
              className="grid h-8 w-8 place-items-center rounded-lg bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]"
            >
              +
            </button>
          </div>
          <span className="text-lg font-bold text-[#0f172a]">:</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMinute(m => (m + 55) % 60)}
              className="grid h-8 w-8 place-items-center rounded-lg bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]"
            >
              −
            </button>
            <span className={`w-10 text-center text-sm font-semibold ${isTimePastToday(hour, minute) ? 'text-[#D64545]' : 'text-[#0f172a]'}`}>
              {String(minute).padStart(2, '0')}
            </span>
            <button
              onClick={() => setMinute(m => (m + 5) % 60)}
              className="grid h-8 w-8 place-items-center rounded-lg bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]"
            >
              +
            </button>
          </div>
        </div>

        {/* Выбранная дата */}
        <div className="mt-2 text-center text-xs text-[#64748b]">
          {formatDisplay()}
          {isPastTime() && selectedDay && (
            <span className="ml-2 text-[#D64545]">— это уже прошло</span>
          )}
        </div>

        {/* Кнопки */}
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => { onChange(''); setSelectedDay(null); onClose(); }}
            className="flex-1 rounded-xl border border-[#e2e8f0] bg-white py-2.5 text-sm text-[#64748b] transition hover:bg-[#f1f5f9]"
          >
            Сбросить
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedDay || isPastTime()}
            className="flex-1 rounded-xl bg-[#374151] py-2.5 text-sm text-white font-medium transition hover:bg-[#1f2937] disabled:opacity-50"
          >
            Готово
          </button>
        </div>
      </div>
    </div>
  );
}
